import {
	ChangeEventHandler,
	FocusEventHandler,
	FunctionComponent,
	useState,
	HTMLInputTypeAttribute,
	MouseEvent,
	useRef,
	useEffect,
} from "react";

import { faInfoCircle, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import { TurnedIn } from "@mui/icons-material";

import { classNames } from "@/utils/classNames";

import { Icon } from "..";
import { Information } from "../Information/Information";
import { ButtonSize } from "../Button/types";

interface InputProps {
	value: string | number | null;
	handleChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	label?: string;
	type?: HTMLInputTypeAttribute;
	information?: string | JSX.Element;
	/** Portal target for the label's info tooltip (extension passes its shadow root). */
	informationPortalContainer?: HTMLElement | null;
	rightInformation?: {
		label: string;
		information: string | JSX.Element | boolean;
	};
	placeholder?: string;
	isOptional?: boolean;
	handleBlur?: FocusEventHandler | ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
	name: string;
	touched?: boolean;
	error?: string | boolean;
	showError?: boolean;
	isDisabled?: boolean;
	autocompleteValue?: string;
	onClick?: (e: MouseEvent) => void;
	testId?: string;
	icon?: "question-mark" | "information" | IconDefinition;
	iconInside?: string | JSX.Element;
	iconInsidePosition?: "left" | "right";
	position?: "top" | "bottom" | "right" | "left";
	className?: string;
	classNameWrapper?: string;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
	autoFocus?: boolean;
	multiline?: boolean;
	min?: number | string;
	max?: number | string;
	required?: boolean;
	classNameLabel?: string;
	maxLength?: number;
	rows?: number;
	/**
	 * Explicit textarea height in px (multiline only). Disables the content
	 * auto-resize — used by drag-resizable composers where the user controls
	 * the height via a grip.
	 */
	heightPx?: number;
	/**
	 * Ceiling for the content auto-resize in px (multiline only, default 300).
	 * Beyond it the textarea keeps its height and scrolls internally. Ignored
	 * when `rows` or `heightPx` disables the auto-resize.
	 */
	maxAutoGrowHeightPx?: number;
	size?: ButtonSize;
	pattern?: string;
	minLength?: number;
	/**
	 * Optional ref to the underlying `<textarea>`/`<input>` element. Useful for
	 * reading the caret position (e.g. inserting a token at the cursor). It is
	 * forwarded alongside the component's internal auto-resize ref.
	 */
	inputRef?: React.Ref<HTMLTextAreaElement | HTMLInputElement>;
}

const assignRef = (
	ref: React.Ref<HTMLTextAreaElement | HTMLInputElement> | undefined,
	node: HTMLTextAreaElement | HTMLInputElement | null
) => {
	if (!ref) {
		return;
	}

	if (typeof ref === "function") {
		ref(node);
	} else {
		(ref as React.MutableRefObject<HTMLTextAreaElement | HTMLInputElement | null>).current = node;
	}
};

export const InputField: FunctionComponent<InputProps> = ({
	isDisabled = false,
	value,
	isOptional,
	handleChange,
	label,
	type = "text",
	information,
	informationPortalContainer,
	rightInformation,
	placeholder,
	handleBlur,
	name,
	error = false,
	touched,
	showError = true,
	autocompleteValue,
	onClick,
	testId,
	iconInside,
	iconInsidePosition = "left",
	position = "top",
	className,
	onKeyDown,
	autoFocus = false,
	multiline = false,
	min,
	max,
	required = false,
	classNameLabel,
	classNameWrapper,
	maxLength,
	rows,
	heightPx,
	maxAutoGrowHeightPx = 300,
	size,
	pattern,
	minLength,
	inputRef,
}) => {
	const [showInformation, setShowInformation] = useState(false);

	const textAreaRef = useRef<HTMLTextAreaElement>(null);

	const randomString =
		Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

	useEffect(() => {
		if (!textAreaRef.current) {
			return;
		}

		const target = textAreaRef.current as HTMLTextAreaElement;

		// An explicit height (drag-resized composer) overrides the auto-resize.
		if (heightPx) {
			target.style.height = `${heightPx}px`;

			return;
		}

		// Skip auto-resize when rows is explicitly set
		if (rows) {
			return;
		}

		target.style.height = "auto";
		const newHeight = Math.min(target.scrollHeight, maxAutoGrowHeightPx);

		target.style.height = `${newHeight}px`;
	}, [value, rows, heightPx, maxAutoGrowHeightPx]);

	return (
		<div className={classNames("mb-3 grow", classNameWrapper || "")}>
			{label && (
				<div
					className={classNames(
						"relative flex items-center  mb-2",
						rightInformation ? "justify-between" : "justify-start"
					)}
				>
					<label
						className={classNames(
							"block text-sm font-medium text-gray-900 appearance-none",
							classNameLabel || ""
						)}
						htmlFor={`${name}-${randomString}`}
					>
						{label}
						{required && <span className="ml-1 text-status-error">*</span>}
						{isOptional && " (Optional)"}
					</label>
					{information && (
						<span className="ml-1">
							<Information information={information} portalContainer={informationPortalContainer} />
						</span>
					)}
					{showInformation && (
						<p
							className={classNames(
								"absolute right-0 left-20 bg-white shadow-all p-3 rounded-md text-xs z-10",
								position === "top" && "-top-[80px]",
								position === "bottom" && "-top-[20px] z-10"
							)}
							onMouseLeave={() => setShowInformation(false)}
						>
							{information}
						</p>
					)}
					{rightInformation && (
						<div className="group cursor-pointer">
							<div className="flex flex-row items-center text-text-meta group">
								<Icon
									className="w-4 h-4 mr-1 cursor-pointer group-hover:text-brand-light"
									icon={faInfoCircle}
								/>
								<span className="text-xs underline group-hover:text-brand-light">
									{rightInformation.label}
								</span>
							</div>
							<p
								className={classNames(
									"hidden absolute right-0 left-20 bg-white shadow-all p-3 rounded-md text-xs z-10 group-hover:block",
									position === "top" && "-top-[80px]",
									position === "bottom" && "-top-[20px] z-10"
								)}
							>
								{rightInformation.information}
							</p>
						</div>
					)}
				</div>
			)}
			<div className="relative">
				{multiline ? (
					<textarea
						ref={(node) => {
							(textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
							assignRef(inputRef, node);
						}}
						autoComplete={autocompleteValue || "off"}
						autoFocus={autoFocus}
						className={classNames(
							"sm:shadow-mb-card sm:border-none min-h-auto scrollbar-hide",
							"input-styles",
							"placeholder-placeholder",
							"text-ssm font-normal",
							rows ? "h-auto resize-y" : "resize-none",
							size === ButtonSize.XS
								? "py-1 px-2 h-[30px]"
								: size === ButtonSize.M
									? "py-2 px-3 !h-[38px]"
									: "py-3 px-4 w-full", // Added padding to account for icon
							iconInside && iconInsidePosition === "left" ? "pl-10" : "pl-3",
							iconInside && iconInsidePosition === "right" ? "pr-10" : "pr-3",
							showError && !!error ? "!border-status-error" : "",
							touched && !error ? "!border-status-success" : "",
							isDisabled && "!border-border cursor-not-allowed opacity-[0.5]",
							className || ""
						)}
						data-test={testId}
						disabled={isDisabled}
						maxLength={maxLength}
						minLength={minLength}
						name={name}
						placeholder={placeholder}
						rows={rows || undefined}
						value={value ?? undefined}
						onBlur={handleBlur}
						onChange={handleChange}
						onClick={onClick}
						onFocus={(e: React.FormEvent<HTMLTextAreaElement>) => {
							const target = e.target as HTMLTextAreaElement;

							// Skip auto-resize when an explicit height or rows is set
							if (!rows && !heightPx) {
								target.style.height = "auto";
								const newHeight = Math.min(target.scrollHeight, maxAutoGrowHeightPx);

								target.style.height = `${newHeight}px`;
							}

							const length = target.value.length;

							target.setSelectionRange(length, length);
						}}
						onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
							// Skip auto-resize when an explicit height or rows is set
							if (rows || heightPx) {
								return;
							}

							const target = e.target as HTMLTextAreaElement;

							target.style.height = "auto";
							const newHeight = Math.min(target.scrollHeight, maxAutoGrowHeightPx);

							target.style.height = `${newHeight}px`;
						}}
						onKeyDown={onKeyDown}
					/>
				) : (
					<input
						ref={(node) => assignRef(inputRef, node)}
						autoComplete={autocompleteValue || "off"}
						autoFocus={autoFocus}
						className={classNames(
							"input-styles",
							"border-border-light",
							"placeholder-placeholder",
							"text-ssm font-normal",
							size === ButtonSize.XS
								? "py-1 px-2 h-[30px]"
								: size === ButtonSize.M
									? "py-2 px-3 !h-[38px]"
									: "py-3 px-4 w-full", // Added padding to account for icon
							iconInside && iconInsidePosition === "left" ? "pl-10" : "pl-3",
							iconInside && iconInsidePosition === "right" ? "pr-10" : "pr-3",
							showError && !!error ? "!border-status-error" : "",
							touched && !error ? "!border-status-success" : "",
							isDisabled && "!border-border cursor-not-allowed opacity-[0.5]",
							className || ""
						)}
						data-test={testId}
						disabled={isDisabled}
						id={`${name}-${randomString}`}
						max={max}
						maxLength={maxLength}
						min={min}
						minLength={minLength}
						name={name}
						pattern={pattern}
						placeholder={placeholder}
						type={type}
						value={value ?? undefined}
						onBlur={handleBlur}
						onChange={handleChange}
						onClick={onClick}
						onKeyDown={onKeyDown}
					/>
				)}
				{iconInside && (
					<div
						className={classNames(
							"absolute inset-y-0 flex items-center",
							iconInsidePosition === "left" ? "left-0 pl-3" : "right-0 pr-3",
							typeof iconInside === "string" ? "pointer-events-none" : ""
						)}
					>
						{typeof iconInside === "string" ? (
							<img alt="Icon" className="w-5 h-5" src={iconInside} />
						) : (
							iconInside
						)}
					</div>
				)}
			</div>
		</div>
	);
};
